import React, { useState } from 'react';
import { Share2, Check, Link2 } from 'lucide-react';

/**
 * Reusable social share widget for quiz/assessment results.
 * Props:
 *   text    — The share message (e.g. "I scored 28/32 on the Stoic Assessment!")
 *   url     — The article URL to share
 *   label   — Button label (default: "Share Result")
 */
export default function ShareResult({ text, url, label = 'Share Result' }) {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);

  const fullUrl = url || window.location.href;
  const encodedText = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(fullUrl);

  const shareLinks = [
    { name: 'X / Twitter', href: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`, color: '#fff' },
    { name: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`, color: '#1877f2' },
    { name: 'LinkedIn', href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, color: '#0a66c2' },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${text}\n${fullUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = `${text}\n${fullUrl}`;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ text, url: fullUrl });
      } catch {}
    } else {
      setOpen(o => !o);
    }
  };

  return (
    <div className="relative inline-block" data-testid="share-result">
      <button
        onClick={handleNativeShare}
        className="px-4 py-2 border border-[#E63946]/40 text-[#E63946] text-xs uppercase tracking-wider hover:bg-[#E63946]/10 transition-colors flex items-center gap-2"
        style={{ fontFamily: 'Bebas Neue, sans-serif' }}
        data-testid="share-result-btn"
      >
        <Share2 className="w-3.5 h-3.5" /> {label}
      </button>

      {open && (
        <div className="absolute bottom-full mb-2 left-0 bg-zinc-900 border border-zinc-700 p-2 min-w-[180px] shadow-xl z-50" data-testid="share-dropdown">
          {shareLinks.map(s => (
            <a
              key={s.name}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              className="block px-3 py-2 text-xs text-zinc-300 hover:text-white hover:bg-zinc-800 transition-colors"
              data-testid={`share-${s.name.toLowerCase().replace(/[\s\/]/g, '-')}`}
              onClick={() => setOpen(false)}
            >
              {s.name}
            </a>
          ))}
          <button
            onClick={copyLink}
            className="w-full text-left px-3 py-2 text-xs text-zinc-300 hover:text-white hover:bg-zinc-800 transition-colors flex items-center gap-2"
            data-testid="share-copy-link"
          >
            {copied ? <><Check className="w-3 h-3 text-[#22c55e]" /> Copied!</> : <><Link2 className="w-3 h-3" /> Copy Link</>}
          </button>
        </div>
      )}
    </div>
  );
}
